import './PasswordGroup.scss'

import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FloatingLabel, Form } from 'react-bootstrap'

import { REMBLE_PASSWORD_MIN_LENGTH } from 'components/auth'
import PasswordRequirement from '../PasswordRequirement'
import { ROUTE_FORGOT_PASSWORD } from 'components/common/helpers/Paths'
import { navigateTo } from 'components/common/navigation'

const TYPE_PASSWORD = 'password'
const TYPE_TEXT = 'text'

/**
 * PasswordGroup component. This component is used to display a password field with requirements and the confirm password field.
 * @param {Function} setPassword The parent component's setPassword function
 * @param {Function} setConfirmPassword The parent component's setConfirmPassword function
 * @param {Boolean} isNewUser Whether or not this is a new user
 * @param {Function} onPasswordsValid The function to call when the passwords are valid
 */
export default function PasswordGroup({
  isNewUser = false,
  isNewPassword = false,
  loading = false,
  showForgotPassword = true,
  onSetPassword = () => {},
  onSetPasswordConfirm = () => {},
  onValidStateChange = () => {},
  onPasswordsValid = () => {},
  handleSubmit = () => {}
}) {
  const history = useHistory()

  // Password
  const [password, setPassword] = useState('')
  const [passwordType, setPasswordType] = useState(TYPE_PASSWORD)
  const [isValidPassword, setIsValidPassword] = useState(true)

  // Password requirements
  const passwordMinLength = REMBLE_PASSWORD_MIN_LENGTH
  const [passwordHasLength, setPasswordHasLength] = useState()
  const [passwordHasLowercase, setPasswordHasLowercase] = useState()
  const [passwordHasUppercase, setPasswordHasUppercase] = useState()
  const [passwordHasNumber, setPasswordHasNumber] = useState()
  const [passwordHasSymbol, setPasswordHasSymbol] = useState()

  // Pasword confirmation
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [isValidPasswordConfirm, setIsValidPasswordConfirm] = useState(true)
  const [confirmPasswordType, setConfirmPasswordType] = useState(TYPE_PASSWORD)

  /**
   * Toggle helper function to swap input type between `text` and `password` on password field
   */
  const togglePasswordVisibility = () =>
    setPasswordType(passwordType === TYPE_PASSWORD ? TYPE_TEXT : TYPE_PASSWORD)

  /**
   * Toggle helper function to swap input type between `text` and `password` on password confirmation field
   */
  const toggleConfirmPasswordVisibility = () =>
    setConfirmPasswordType(
      confirmPasswordType === TYPE_PASSWORD ? TYPE_TEXT : TYPE_PASSWORD
    )

  /**
   * Sets and runs validation on `password` field. Ensures that the password matches the password policy requirements
   * Does password match our password policy?
   * 8 character min length
   * At least 1 uppercase char
   * At least 1 lowercase char
   * At least 1 number
   * At least 1 symbol (^$*.[]{}()? "!@#%&/\,><':;|_~`+=-)
   * @param {String} value The field's value
   */
  const setAndValidatePassword = value => {
    // Short circuit validation if the user is an existing user
    // and we're not changing their password
    if (!isNewUser && !isNewPassword) {
      setPassword(value)
      setIsValidPassword(!!value)
      onSetPassword(value)
      onValidStateChange(!!value)
      return
    }

    const hasLength = value.length > passwordMinLength - 1
    const hasUppercase = /[A-Z]/.test(value)
    const hasLowercase = /[a-z]/.test(value)
    const hasNumber = /[0-9]/.test(value)
    const hasSymbol = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(value)

    const isValid =
      hasLength && hasUppercase && hasLowercase && hasNumber && hasSymbol

    // If we update the password, let's update the confirmation
    if (passwordConfirm.length)
      setIsValidPasswordConfirm(value === passwordConfirm)

    setPassword(value)
    onSetPassword(value)
    setPasswordHasLength(hasLength)
    setPasswordHasUppercase(hasUppercase)
    setPasswordHasLowercase(hasLowercase)
    setPasswordHasNumber(hasNumber)
    setPasswordHasSymbol(hasSymbol)
    setIsValidPassword(isValid)

    // If the user is new and the password is valid,
    // let's trigger a `blur` event to automatically show the next field
    if ((isNewUser || isNewPassword) && isValid) handlePasswordBlur()

    // Update if we went back
    onValidStateChange(
      !!passwordConfirm.length && isValidPasswordConfirm && isValid
    )
  }

  /**
   * Sets and runs validation on `passwordConfirm` field
   * @param {String} value The field's value
   */
  const setAndValidatePasswordConfirm = value => {
    const isValid = value === password

    setPasswordConfirm(value)
    onSetPasswordConfirm(value)
    setIsValidPasswordConfirm(isValid)

    // If the user is new and the password confirmation is valid,
    // let's trigger a `blur` event to automatically show the next field
    if ((isNewUser || isNewPassword) && isValid)
      handlePasswordConfirmBlur(false, true)

    // Update if we went back
    onValidStateChange(!!password.length && isValidPassword && isValid)
  }

  /**
   * Helper function to handle `blur` on `password` field
   */
  const handlePasswordBlur = (isEnterKey = false) => {
    // All set to continue
    const shouldProceedWithSignup =
      (isNewUser || isNewPassword) && password && isValidPassword

    if (isEnterKey) {
      // If we're showing the login form and the user hit the ENTER key, let's submit the form
      if (!isNewUser && !isNewPassword) return handleSubmit()

      // Show and focus the password confirmation field
      if (shouldProceedWithSignup)
        document.getElementById('floatingPasswordConfirm')?.focus()
    }
  }

  /**
   * Helper function to handle `blur` on `passwordConfirm` field
   * @param {Boolean} isEnterKey Whether the user hit the ENTER key
   * @param {Boolean} shouldProceedWithSignup Whether to go ahead and continue with the signup
   */
  const handlePasswordConfirmBlur = (
    isEnterKey = false,
    shouldProceedWithSignup
  ) => {
    shouldProceedWithSignup =
      shouldProceedWithSignup || (passwordConfirm && isValidPasswordConfirm)

    // If our password confirmation looks good, let's show and focus the name field
    if (shouldProceedWithSignup) onPasswordsValid(isEnterKey)
  }

  /**
   * Helper function to handle clicks on the forgot password link
   */
  const handleForgotPasswordClick = () => {
    // Return early if we're loading
    if (loading) return

    // Navigate to forgot password page
    navigateTo(ROUTE_FORGOT_PASSWORD, history)
  }

  return (
    <div className='password-group'>
      <Form.Group controlId='formPassword'>
        <FloatingLabel
          label={`${
            isNewUser
              ? 'Create a password'
              : 'Your' + (isNewPassword ? ' new ' : ' ') + 'password'
          }`}
        >
          <span
            className='visibility-toggle'
            onClick={() => togglePasswordVisibility()}
          >
            {passwordType === TYPE_PASSWORD ? (
              <>
                <i className='icon bi-eye-slash'></i> Show
              </>
            ) : (
              <>
                <i className='icon bi-eye'></i> Hide
              </>
            )}
          </span>
          <Form.Control
            name='password'
            id='user-password'
            disabled={loading}
            size='lg'
            placeholder={`${
              isNewUser
                ? 'Create a password'
                : 'Your' + (isNewPassword ? ' new ' : ' ') + 'password'
            }`}
            required
            type={passwordType}
            onChange={e => setAndValidatePassword(e.currentTarget.value)}
            // Only show password confirmation field once we have a valid password
            onBlur={e => {
              // Run validation again, in case user came back to the field
              setAndValidatePassword(e.currentTarget.value)
              handlePasswordBlur()
            }}
            onKeyDown={e => e.key === 'Enter' && handlePasswordBlur(true)}
            isInvalid={!isValidPassword}
            isValid={password && isValidPassword}
          />

          {/* Show forgot password? */}
          {showForgotPassword && (
            <span
              className='forgot-password-link'
              onClick={handleForgotPasswordClick}
            >
              Forgot password
            </span>
          )}
          <Form.Control.Feedback type='invalid'>
            Please provide a valid password
            {(isNewUser || isNewPassword) && (
              <ul className='password-requirements'>
                {PasswordRequirement(
                  passwordHasLength,
                  passwordHasLength
                    ? `${passwordMinLength} character min length.`
                    : `${passwordMinLength} character min length. Needs ${
                        passwordMinLength - password.length
                      } more.`
                )}
                {PasswordRequirement(
                  passwordHasUppercase,
                  'At least 1 uppercase letter'
                )}
                {PasswordRequirement(
                  passwordHasLowercase,
                  'At least 1 lowercase letter'
                )}
                {PasswordRequirement(passwordHasNumber, 'At least 1 number')}
                {PasswordRequirement(
                  passwordHasSymbol,
                  'At least 1 special character (e.g., "&!.@", etc.)'
                )}
              </ul>
            )}
          </Form.Control.Feedback>
          <Form.Control.Feedback>
            {(isNewUser || isNewPassword) &&
              "That's a good looking password. A secure password keeps your account safe. 🙂"}
          </Form.Control.Feedback>
        </FloatingLabel>
      </Form.Group>

      {/* Only show password confirmation once we have a valid password, or if it's a new password */}
      <Form.Group
        className={`form-group ${isNewUser || isNewPassword ? 'visible' : ''}`}
        controlId='formPasswordConfirm'
      >
        <FloatingLabel
          controlId='floatingPasswordConfirm'
          label={`Confirm your ${isNewPassword ? 'new' : ''} password`}
        >
          <span
            className='visibility-toggle'
            onClick={() => toggleConfirmPasswordVisibility()}
          >
            {confirmPasswordType === TYPE_PASSWORD ? (
              <>
                <i className='icon bi-eye-slash'></i> Show
              </>
            ) : (
              <>
                <i className='icon bi-eye'></i> Hide
              </>
            )}
          </span>
          <Form.Control
            name='password_confirm'
            disabled={loading}
            size='lg'
            label={`Confirm your ${isNewPassword ? 'new' : ''} password`}
            placeholder={`Confirm your ${isNewPassword ? 'new' : ''} password`}
            required
            type={confirmPasswordType}
            onChange={e => setAndValidatePasswordConfirm(e.currentTarget.value)}
            onBlur={() => handlePasswordConfirmBlur()}
            onKeyDown={e =>
              e.key === 'Enter' && handlePasswordConfirmBlur(true)
            }
            isInvalid={!isValidPasswordConfirm}
            isValid={passwordConfirm && isValidPasswordConfirm}
          />
          <Form.Control.Feedback type='invalid'>
            Your passwords do not match
          </Form.Control.Feedback>
          <Form.Control.Feedback>Passwords match 🔐</Form.Control.Feedback>
        </FloatingLabel>
      </Form.Group>
    </div>
  )
}
